import { ElementRef, PropsWithChildren, useLayoutEffect } from 'react';
import { css } from '@emotion/react';
import composeRefs from '@seznam/compose-react-refs';
import { motion } from 'motion/react';
import { theme } from '@frontend/theme';
import { useScrollShadow } from '@frontend/design-system';
import { useCalendarView } from './providers';
import { useScrollPreservation } from './zoom';

type CalendarViewMainWrapperProps = {
  containerRef: React.RefObject<ElementRef<'article'>>;
  id?: string;
  disableVerticalScroll?: boolean;
};

export const CalendarViewMainWrapper = ({
  children,
  containerRef,
  id,
}: PropsWithChildren<CalendarViewMainWrapperProps>) => {
  const { clientWidth, isLeft, isRight, isScrollable, scrollRef } = useScrollShadow(true);
  const { setContainerState, setScrollContainerRef } = useCalendarView(['setContainerState', 'setScrollContainerRef']);
  const { scrollRef: preservationScrollRef } = useScrollPreservation();

  useLayoutEffect(() => {
    setContainerState({ isLeft, isRight, isScrollable, clientWidth });
  }, [isLeft, isRight, isScrollable, clientWidth, setContainerState]);

  // Register calendar container with the context for auto-scroll
  useLayoutEffect(() => {
    if (containerRef?.current) {
      setScrollContainerRef(containerRef.current);
    }
    return () => setScrollContainerRef(null);
  }, [setScrollContainerRef, containerRef]);

  return (
    <motion.article
      id={id}
      layoutScroll
      ref={composeRefs(containerRef, scrollRef, preservationScrollRef)}
      css={containerStyle}
    >
      {children}
    </motion.article>
  );
};

const containerStyle = css({
  overflow: 'auto',
  isolation: 'isolate',
  height: '100%',
  width: '100%',
  display: 'grid',
  backgroundColor: theme.colors.neutral5,
  position: 'relative',
  overscrollBehavior: 'contain',
  gridTemplateRows: 'auto 1fr',
  gridTemplateColumns: 'auto',
  flex: 1,
});
